import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Renders a long list in chunks: shows `pageSize` items, then appends the next chunk
 * when the sentinel element scrolls into view. Resets to the first chunk when `resetKey` changes
 * (e.g. filter / search / company change).
 */
export function useIncrementalList(items = [], { pageSize = 30, resetKey = "" } = {}) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const observerRef = useRef(null);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [resetKey, pageSize]);

  const total = items.length;
  const hasMore = visibleCount < total;

  const showMore = useCallback(() => {
    setVisibleCount((count) => Math.min(count + pageSize, total));
  }, [pageSize, total]);

  // Callback ref: (re)attach an IntersectionObserver whenever the sentinel mounts or a chunk is appended.
  // Re-observing fires immediately if the sentinel is still on screen (short chunks on tall screens).
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node || typeof IntersectionObserver === "undefined") return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) showMore();
      },
      { rootMargin: "400px 0px" }
    );
    observerRef.current.observe(node);
  }, [showMore, visibleCount]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return {
    visibleItems: hasMore ? items.slice(0, visibleCount) : items,
    visibleCount: Math.min(visibleCount, total),
    total,
    hasMore,
    showMore,
    sentinelRef
  };
}

export default useIncrementalList;
